import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { heldRowIds, isPackArrivalRow, packRevealFallbackMs, readerInterrupted, type PackArrivalRow } from './pack-arrival-reveal';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches;
}

export interface PackArrivalReveal {
  /**
   * Rows that still have to type themselves in. A row leaves once it has been revealed, so a row the
   * transcript remounts later (a reload of the same window) is drawn at once instead of typing again.
   */
  typing: ReadonlySet<string>;
  /** Staged rows that are finished: one still typing when it lands here jumps to its full words. */
  revealed: ReadonlySet<string>;
  /** Rows not drawn yet, because a row before them is still typing. */
  held: ReadonlySet<string>;
  /**
   * How far each row has typed. The transcript can remount its rows mid-reveal (the history read
   * replacing a first paint), and a row picks up from here instead of typing its words a second time.
   */
  progress: Map<string, number>;
  /** A staged row reports that it has finished. Stable, so a row's timer is not reset by re-renders. */
  onRevealed: (id: string) => void;
}

/**
 * The reveal of a pack walkthrough that just arrived — see `lib/pack-arrival-reveal`.
 *
 * `handoffPending` is `usePackHandoff()`. The moment it first turns true is taken as when the handoff
 * armed: the hook flips it in the same commit that consumes the URL marker, so the two are one moment.
 */
export function usePackArrivalReveal(
  handoffPending: boolean,
  rows: ReadonlyArray<PackArrivalRow & { content?: string }>,
): PackArrivalReveal {
  const armedAtRef = useRef<number | null>(null);
  if (handoffPending && armedAtRef.current === null) armedAtRef.current = Date.now();
  const armedAt = handoffPending ? armedAtRef.current : null;

  // Decided during render, not in an effect: a staged row has to MOUNT staged, because its typewriter
  // reads the flag once. Idempotent per id, so a repeated render decides nothing new.
  const decided = useRef(new Set<string>());
  const stagedRef = useRef(new Set<string>());
  for (const row of rows) {
    if (!row.id || decided.current.has(row.id)) continue;
    decided.current.add(row.id);
    if (armedAt !== null && !prefersReducedMotion() && isPackArrivalRow(row, armedAt)) stagedRef.current.add(row.id);
  }
  const staged = stagedRef.current;

  const progress = useRef(new Map<string, number>()).current;
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());
  const onRevealed = useCallback((id: string) => {
    setRevealed((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const held = useMemo(() => heldRowIds(rows, staged, revealed), [rows, staged, revealed]);
  const interrupted = readerInterrupted(rows, staged, revealed);
  useEffect(() => {
    if (interrupted) setRevealed((prev) => new Set([...prev, ...staged]));
  }, [interrupted, staged]);
  const typingRows = useMemo(() => new Set([...staged].filter((id) => !revealed.has(id))), [rows, staged, revealed]);

  const active = staged.size > 0 ? rows.find((r) => !!r.id && staged.has(r.id) && !revealed.has(r.id)) : undefined;
  const typingId = active?.id;
  const typingLength = active?.content?.length ?? 0;
  useEffect(() => {
    if (!typingId) return undefined;
    const timer = setTimeout(() => onRevealed(typingId), packRevealFallbackMs(typingLength));
    return () => clearTimeout(timer);
    // The length is read once per row: a row still streaming must not keep pushing its own deadline out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typingId, onRevealed]);

  return { typing: typingRows, revealed, held, progress, onRevealed };
}
